import React, { useState, useEffect } from 'react';
import { Smartphone, Monitor, Globe, Download, CheckCircle, Clock, Shield, ChevronDown, ChevronUp, Apple } from 'lucide-react';
import { API_BASE } from '../../config/apiBase';

export default function AppsPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/app-download/config`)
      .then(r => r.ok ? r.json() : {})
      .then(d => setConfig(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-5 h-5 border-2 rounded-full" style={{ borderColor: 'var(--sv-gold-primary)', borderTopColor: 'transparent' }} /></div>;

  const c = config || {};

  const platforms = [
    {
      id: 'android',
      name: 'Android',
      icon: Smartphone,
      available: c.android_available,
      version: c.app_version || '1.0.0',
      build: c.app_build || '1',
      size: c.app_size || '~8 MB',
      updated: c.app_updated_at || '-',
      changelog: c.app_changelog || '',
      downloadUrl: c.direct_download_active ? `${API_BASE}${c.direct_download_url}` : null,
      ctaText: c.cta_text || 'Baixar APK',
      color: '#3DDC84',
    },
    {
      id: 'ios',
      name: 'iOS',
      icon: Apple,
      available: c.ios_available,
      version: '-',
      ctaText: c.coming_soon_text || 'Em breve',
      color: '#007AFF',
    },
    {
      id: 'desktop',
      name: 'Desktop',
      icon: Monitor,
      available: c.desktop_available,
      version: '-',
      ctaText: c.coming_soon_text || 'Em breve',
      color: '#8B5CF6',
    },
    {
      id: 'web',
      name: 'Web App',
      icon: Globe,
      available: true,
      version: 'Sempre atualizado',
      ctaText: 'Usar no navegador',
      downloadUrl: 'https://app.supervitrineofertas.com',
      color: 'var(--sv-gold-primary)',
      isWeb: true,
    },
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="apps-page">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>{c.hero_title || 'SuperVitrine no seu bolso'}</h2>
        <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{c.hero_subtitle || ''}</p>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {platforms.map(p => (
          <div key={p.id} className="rounded-xl p-4 transition-all" style={{ background: 'var(--surface)', border: `1px solid ${p.available ? p.color + '30' : 'var(--border)'}`, opacity: p.available ? 1 : 0.6 }}
            data-testid={`platform-card-${p.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}15` }}>
                <p.icon size={20} style={{ color: p.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{p.name}</span>
                  {p.available ? (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E' }}>
                      <CheckCircle size={8} /> Disponivel
                    </span>
                  ) : (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" style={{ background: 'rgba(107,114,128,0.1)', color: '#6B7280' }}>
                      <Clock size={8} /> Em breve
                    </span>
                  )}
                </div>

                {p.available && p.id === 'android' && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px]" style={{ color: 'var(--muted)' }}>
                      <span>Versao: <strong style={{ color: 'var(--text)' }}>{p.version}</strong></span>
                      <span>Build: <strong style={{ color: 'var(--text)' }}>{p.build}</strong></span>
                      <span>Tamanho: <strong style={{ color: 'var(--text)' }}>{p.size}</strong></span>
                      <span>Atualizado: <strong style={{ color: 'var(--text)' }}>{p.updated}</strong></span>
                    </div>
                    {p.changelog && (
                      <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                        <span style={{ color: 'var(--sv-gold-primary)' }}>Changelog:</span> {p.changelog}
                      </p>
                    )}
                  </div>
                )}

                {p.available && p.isWeb && (
                  <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Acesse pelo navegador sem instalar nada.</p>
                )}

                {!p.available && (
                  <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Estamos trabalhando nesta versao.</p>
                )}

                {/* CTA Button */}
                <div className="mt-3">
                  {p.available && p.downloadUrl ? (
                    p.id === 'android' ? (
                    <a
                      href="/baixar-app?from=system"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 no-underline"
                      style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                      data-testid={`download-btn-${p.id}`}
                    >
                      <Download size={12} />
                      {p.ctaText}
                    </a>
                    ) : (
                    <a
                      href={p.downloadUrl}
                      target={p.isWeb ? '_blank' : undefined}
                      rel={p.isWeb ? 'noopener noreferrer' : undefined}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 no-underline"
                      style={{ background: p.color, color: '#000' }}
                      data-testid={`download-btn-${p.id}`}
                    >
                      {p.isWeb ? <Globe size={12} /> : <Download size={12} />}
                      {p.ctaText}
                    </a>
                    )
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                      <Clock size={12} /> {p.ctaText}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Installation Instructions */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <button
          className="w-full flex items-center justify-between p-3 text-xs font-medium"
          style={{ color: 'var(--text)' }}
          onClick={() => setShowInstructions(!showInstructions)}
          data-testid="install-instructions-toggle"
        >
          <span className="flex items-center gap-2"><Shield size={14} style={{ color: 'var(--sv-gold-primary)' }} /> Como instalar o APK no Android</span>
          {showInstructions ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showInstructions && (
          <div className="px-3 pb-3 space-y-2 text-[11px]" style={{ color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
            <div className="pt-2 space-y-1.5">
              <p><strong style={{ color: 'var(--sv-gold-primary)' }}>1.</strong> Toque em <strong>"Baixar APK"</strong> acima</p>
              <p><strong style={{ color: 'var(--sv-gold-primary)' }}>2.</strong> Se o celular bloquear, va em <strong>Configuracoes &gt; Seguranca &gt; Fontes desconhecidas</strong> e permita a instalacao</p>
              <p><strong style={{ color: 'var(--sv-gold-primary)' }}>3.</strong> Abra o arquivo baixado e toque em <strong>Instalar</strong></p>
              <p><strong style={{ color: 'var(--sv-gold-primary)' }}>4.</strong> Pronto! O SuperVitrine aparecera na sua tela inicial</p>
            </div>
            <div className="rounded-lg p-2 mt-2" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
              <p className="text-[10px]"><strong style={{ color: 'var(--text)' }}>Seguranca:</strong> O APK e assinado e seguro. O app conecta diretamente com a API oficial do SuperVitrine.</p>
            </div>
          </div>
        )}
      </div>

      {/* Note */}
      <p className="text-[10px] text-center" style={{ color: 'var(--muted)' }}>
        {c.note_text || 'Download seguro e gratuito'}
      </p>
    </div>
  );
}
